import { Button } from "@/components/ui/button";
import { ContentLoader } from "@/components/ui/loaders";
import { ActionBar } from "@/components/layout/app.layout";
import { FlavorCard } from "@/components/fornada/campaign";
import { SelectField } from "@/components/fornada/controls";
import { Display, Label, Numeral, ScreenTitle, Surface } from "@/components/fornada/primitives";
import { useActiveSale, useFlavors } from "@/hooks/use-active-sale";
import { useCreateOrder } from "@/hooks/use-create-order";
import { useUpdateOrder } from "@/hooks/use-update-order";
import { useUsers } from "@/hooks/use-users";
import { useToast } from "@/providers/toast.provider";
import { useAuth } from "@/providers/auth.provider";
import { flavorDescriptions, isOrdersOpen, orderPositions } from "@/lib/sale";
import { orderCode, pad2 } from "@/lib/format";
import type { SaleWithRelations } from "@/types/sale-with-relations";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

type SaleOrder = SaleWithRelations["orders"][number];

/** Novo pedido (`/orders/new`) ou edição de um existente (`/orders/:id/edit`). */
const OrderFormPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: sale, isLoading } = useActiveSale();
  const isAdmin = user?.role === "ADMIN";

  if (isLoading) return <ContentLoader show />;
  // Depois do prazo, só o ADMIN registra ou altera pedidos.
  if (!sale || (!isOrdersOpen(sale) && !isAdmin)) return <Navigate to="/" replace />;

  const order = id ? sale.orders.find((o) => o.id === id) : undefined;
  if (id && (!order || (!isAdmin && order.userId !== user?.id)))
    return <Navigate to="/orders" replace />;

  // `key`: o formulário reinicia o estado ao trocar de pedido.
  return <OrderForm key={id ?? "new"} sale={sale} order={order} />;
};

function OrderForm({ sale, order }: { sale: SaleWithRelations; order?: SaleOrder }) {
  const navigate = useNavigate();
  const { toaster } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const { data: flavors = [] } = useFlavors();
  const { data: sellers = [] } = useUsers(isAdmin);
  const { mutate: createOrder, isPending: creating } = useCreateOrder();
  const { mutate: updateOrder, isPending: updating } = useUpdateOrder();
  const isPending = creating || updating;

  const [qty, setQty] = useState<Record<string, number>>(() =>
    Object.fromEntries(order?.items.map((i) => [i.flavorId, i.quantity]) ?? []),
  );
  const [obs, setObs] = useState<Record<string, string>>(() =>
    Object.fromEntries(order?.items.map((i) => [i.flavorId, i.obs || order?.obs || ""]) ?? []),
  );
  const [userId, setUserId] = useState(order?.userId ?? user?.id ?? "");

  const descriptions = flavorDescriptions(flavors);
  const picked = flavors.filter((f) => (qty[f.id] ?? 0) > 0);
  const total = picked.reduce((n, f) => n + (qty[f.id] ?? 0), 0);

  // Garante que o dono atual aparece no select mesmo antes da lista carregar.
  const sellerOptions = sellers.length
    ? sellers.map((s) => ({ value: s.id, label: s.id === user?.id ? `${s.name} (você)` : s.name }))
    : [{ value: userId, label: order?.user?.name ?? user?.name ?? "" }];

  const save = () => {
    if (!total || isPending) return;
    const data = {
      items: picked.map((f) => ({
        flavorId: f.id,
        quantity: qty[f.id]!,
        obs: obs[f.id]?.trim() || undefined,
      })),
      userId: isAdmin ? userId : undefined,
    };
    const onSuccess = () => {
      toaster(order ? "Pedido atualizado" : "Pedido salvo", "success");
      navigate("/orders", { replace: true });
    };
    if (order) updateOrder({ id: order.id, ...data }, { onSuccess });
    else createOrder(data, { onSuccess });
  };

  const saveButton = (
    <Button
      size="xl"
      className="flex-1 min-[960px]:h-[52px] min-[960px]:w-full min-[960px]:flex-none"
      disabled={!total || isPending}
      onClick={save}
    >
      {order ? "Salvar alterações" : "Salvar pedido"}
    </Button>
  );

  return (
    <div className="grid items-start gap-6 min-[960px]:grid-cols-[minmax(0,1fr)_360px] min-[960px]:gap-8">
      <div className="flex flex-col gap-5">
        <Link
          to="/orders"
          className="-mb-2 flex size-11 items-center justify-center rounded-md text-fx-body hover:bg-fx-raised min-[960px]:hidden"
          aria-label="Voltar"
        >
          <ArrowLeft size={22} strokeWidth={1.6} />
        </Link>
        <ScreenTitle
          eyebrow={sale.name}
          title={
            order
              ? `Pedido ${orderCode(orderPositions(sale.orders).get(order.id) ?? 0)}`
              : "Novo pedido"
          }
          subtitle={
            order
              ? "Ajuste as quantidades ou acrescente sabores."
              : "Escolha os sabores e a quantidade de cada um."
          }
        />

        {isAdmin && (
          <SelectField
            label="Vendedor"
            value={userId}
            onChange={setUserId}
            options={sellerOptions}
            className="min-[960px]:max-w-sm"
          />
        )}

        <div className="flex flex-col gap-2 min-[960px]:grid min-[960px]:grid-cols-3 min-[960px]:gap-3">
          <Label className="mb-1 min-[960px]:hidden">Escolha os sabores</Label>
          {flavors.map((f) => (
            <FlavorCard
              key={f.id}
              name={f.name}
              description={descriptions.get(f.id)}
              quantity={qty[f.id] ?? 0}
              onChange={(n) => setQty((q) => ({ ...q, [f.id]: n }))}
              obs={obs[f.id] ?? ""}
              onObsChange={(v) => setObs((o) => ({ ...o, [f.id]: v }))}
            />
          ))}
        </div>
      </div>

      {/* ≥ 960px: resumo fixo */}
      <Surface hero className="sticky top-24 hidden flex-col gap-4 border-fx-line p-[22px] min-[960px]:flex">
        <Display size="h4" as="h2">
          Resumo
        </Display>
        <ul className="flex flex-col gap-2">
          {picked.map((f) => (
            <li key={f.id} className="flex flex-col gap-0.5">
              <span className="flex items-baseline gap-2.5">
                <span className="fx-num w-7 text-base text-fx-cream">{qty[f.id]}×</span>
                <span className="flex-1 text-sm font-medium text-fx-body">{f.name}</span>
              </span>
              {obs[f.id]?.trim() && (
                <span className="pl-[38px] text-[12.5px] italic text-fx-secondary">
                  "{obs[f.id]!.trim()}"
                </span>
              )}
            </li>
          ))}
          {picked.length === 0 && (
            <li className="text-[13px] text-fx-faint">Nenhum sabor escolhido.</li>
          )}
        </ul>
        <div className="flex items-end justify-between border-t border-fx-line-subtle pt-3.5">
          <Label>Total de pizzas</Label>
          <Numeral cream className="text-4xl leading-[30px]">
            {pad2(total)}
          </Numeral>
        </div>
        {saveButton}
      </Surface>

      {/* < 960px: barra de ação */}
      <ActionBar className="min-[960px]:hidden">
        <div className="flex items-end gap-2">
          <Numeral cream className="text-[30px] leading-[30px]">
            {pad2(total)}
          </Numeral>
          <Label className="pb-0.5">Pizzas</Label>
        </div>
        {saveButton}
      </ActionBar>
    </div>
  );
}

export default OrderFormPage;
